import type { FC, ComponentProps, PropsWithChildren, ReactNode } from 'react';
import React, { isValidElement } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { ByLine, ByLineFromFragment } from '@confluence/content-header/entry-points/ByLine.ts';
import type { BylineClassification_content$key } from '@confluence/content-header/entry-points/BylineClassificationFragmentType.ts';
import type { ByLineContributors_contentContributors$key } from '@confluence/content-header/entry-points/ByLineContributorsFragmentType.ts';
import {
	ContentTitle,
	ContentTitleFromFragment,
} from '@confluence/content-header/entry-points/ContentTitle.ts';
import type { ContentTitle_space$key } from '@confluence/content-header/entry-points/ContentTitleSpaceFragmentType.ts';
import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import {
	getTitleContentProperty,
	useTitleContentPropertiesForPublishedPage,
} from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expVal, expValEquals, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { ApprovalBanner } from '@confluence/approvals/entry-points/ApprovalBanner.ts';
import { LazyMeetingNotesAutomationDiscoveryFragment } from '@confluence/automation-discovery/entry-points/MeetingNotesAutomationDiscoveryFragment.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { useViewPageParams } from './useViewPageParams.ts';
import type { ContentTitleWithByLine_content$key } from './__generated__/ContentTitleWithByLine_content.graphql.ts';

export const ContentTitleWithByLineFragment: GraphQLTaggedNode = graphql`
	fragment ContentTitleWithByLine_content on Content
	@argumentDefinitions(
		useNewContentTopper: { type: "Boolean!", defaultValue: false }
		includeConnectionAncestors: { type: "Boolean!", defaultValue: false }
	) {
		...ContentTitle_content @arguments(useNewContentTopper: $useNewContentTopper)
		...ByLine_content
			@arguments(
				useNewContentTopper: $useNewContentTopper
				includeConnectionAncestors: $includeConnectionAncestors
			)
		...ArchivedPageBannerV2Status_content
		...MeetingNotesAutomationDiscoveryFragment_content
	}
`;

type ByLinePassThroughProps = Pick<
	ComponentProps<typeof ByLine>,
	'hasByLineContributors' | 'hasByLineExtensions'
>;

type BylineClassificationFragmentProp = {
	bylineClassificationFragmentRef?: BylineClassification_content$key | null;
};

type AnalyticsBylineFragmentProp = {
	analyticsBylineFragmentRef?: unknown;
};

// Downstream packages are checked against the last generated content-header declaration.
const ByLineWithClassificationFragment = ByLine as FC<
	ComponentProps<typeof ByLine> & BylineClassificationFragmentProp & AnalyticsBylineFragmentProp
>;
const ByLineFromFragmentWithClassificationFragment = ByLineFromFragment as FC<
	ComponentProps<typeof ByLineFromFragment> &
		BylineClassificationFragmentProp &
		AnalyticsBylineFragmentProp
>;

type Props = Partial<ByLinePassThroughProps> &
	ComponentProps<typeof ContentTitle> & {
		hasCoverPicture?: boolean;
		pageWidthType?: string;
		isEmbeddedPage?: boolean;
		shouldShowBylineReactions?: boolean;
		contentType?: string | null;
		spaceId?: string | null;
		contentSubType?: string | null;
		hasByLine?: boolean;
		contentFragmentRef?: ContentTitleWithByLine_content$key | null;
		byLineContributors?: ByLineContributors_contentContributors$key | null;
		titleSpaceFragmentRef?: ContentTitle_space$key | null;
		bylineClassificationFragmentRef?: BylineClassification_content$key | null;
		contentClassificationFragmentRef?: ComponentProps<
			typeof ByLine
		>['contentClassificationFragmentRef'];
		reactionsContentFragmentRef?: ComponentProps<typeof ByLine>['reactionsContentFragmentRef'];
		meetingNotesAutomationDiscoveryFragmentRef?: ComponentProps<
			typeof LazyMeetingNotesAutomationDiscoveryFragment
		>['contentFragmentRef'];
		analyticsBylineFragmentRef?: AnalyticsBylineFragmentProp['analyticsBylineFragmentRef'];
		hasDocumentContractAnalyticsByline?: boolean;
	};

const BylineCenterStyles = xcss({
	justifyContent: 'center',
});

const ApprovalBannerStyles = xcss({
	marginBlockEnd: 'space.200',
});

const styles = cssMap({
	titleAreaBannerWrapper: {
		marginBlockEnd: token('space.200'),
	},
});

const TitleAreaBannerWrapper: FC<PropsWithChildren> = ({ children }) => (
	<Box xcss={styles.titleAreaBannerWrapper}>{children}</Box>
);

export const ContentTitleWithByLine: FC<Props> = (props) =>
	// Preserve the eligible byline render as the client exposure point.
	// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
	UNSAFE_expValNoExposure('confluence_ssr_byline', 'isEnabled', false) ? (
		<ContentTitleWithByLineInner {...props} />
	) : (
		<SuspenseWithDataFetcher dataFetcherName="byline-apps">
			<ContentTitleWithByLineInner {...props} />
		</SuspenseWithDataFetcher>
	);

// Construct the byline boundary in the hook-free composition component. Title
// readers receive it as a slot; byline hooks run inside the complete bounded reader.
type ByLineSlot = { byLine?: ReactNode };
type FragmentProps = Props & {
	contentFragmentRef: ContentTitleWithByLine_content$key;
	analyticsBylineQuerySource: 'standalone' | 'documentFragment';
};
type FragmentByLineSlot = {
	renderByLine?: (
		contentFragmentRef: ComponentProps<typeof ByLineFromFragment>['contentFragmentRef'],
	) => ReactNode;
};

const ContentTitleWithByLineInner: FC<Props> = ({
	contentFragmentRef,
	byLineContributors,
	titleSpaceFragmentRef,
	bylineClassificationFragmentRef,
	contentClassificationFragmentRef,
	reactionsContentFragmentRef,
	analyticsBylineFragmentRef,
	hasDocumentContractAnalyticsByline = false,
	...props
}: Props) => {
	if (
		contentFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		const analyticsBylineQuerySource =
			hasDocumentContractAnalyticsByline &&
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader')
				? 'documentFragment'
				: 'standalone';
		return (
			<ContentTitleWithByLineFragmentReader
				{...props}
				contentFragmentRef={contentFragmentRef}
				byLineContributors={byLineContributors}
				titleSpaceFragmentRef={titleSpaceFragmentRef}
				bylineClassificationFragmentRef={bylineClassificationFragmentRef}
				contentClassificationFragmentRef={contentClassificationFragmentRef}
				reactionsContentFragmentRef={reactionsContentFragmentRef}
				analyticsBylineFragmentRef={analyticsBylineFragmentRef}
				analyticsBylineQuerySource={analyticsBylineQuerySource}
				renderByLine={
					// The title reader unwraps the container once, then supplies its masked
					// byline ref. The bounded child reads only its own ByLine_content fragment.
					// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
					UNSAFE_expValNoExposure('confluence_ssr_byline', 'isEnabled', false)
						? (byLineFragmentRef) => (
								<SuspenseWithDataFetcher dataFetcherName="byline-apps">
									<ByLineFromFragmentWithClassificationFragment
										contentId={props.contentId}
										contentFragmentRef={byLineFragmentRef}
										contentContributors={byLineContributors}
										bylineClassificationFragmentRef={bylineClassificationFragmentRef}
										contentClassificationFragmentRef={contentClassificationFragmentRef}
										reactionsContentFragmentRef={reactionsContentFragmentRef}
										analyticsBylineFragmentRef={analyticsBylineFragmentRef}
										analyticsBylineQuerySource={analyticsBylineQuerySource}
										hasByLineContributors={props.hasByLineContributors}
										hasByLineExtensions={props.hasByLineExtensions}
										shouldShowBylineReactions={props.shouldShowBylineReactions}
										contentType={props.contentType}
										contentSubType={props.contentSubType}
										spaceId={props.spaceId}
									/>
								</SuspenseWithDataFetcher>
							)
						: undefined
				}
			/>
		);
	}

	return (
		<ContentTitleWithByLineQuery
			{...props}
			bylineClassificationFragmentRef={bylineClassificationFragmentRef}
			contentClassificationFragmentRef={contentClassificationFragmentRef}
			reactionsContentFragmentRef={reactionsContentFragmentRef}
			analyticsBylineFragmentRef={analyticsBylineFragmentRef}
			byLine={
				// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
				UNSAFE_expValNoExposure('confluence_ssr_byline', 'isEnabled', false) ? (
					<SuspenseWithDataFetcher dataFetcherName="byline-apps">
						<ContentByLineQuery
							{...props}
							bylineClassificationFragmentRef={bylineClassificationFragmentRef}
							contentClassificationFragmentRef={contentClassificationFragmentRef}
							reactionsContentFragmentRef={reactionsContentFragmentRef}
							analyticsBylineFragmentRef={analyticsBylineFragmentRef}
						/>
					</SuspenseWithDataFetcher>
				) : undefined
			}
		/>
	);
};

const ContentByLineQuery: FC<Props> = ({
	contentId,
	hasByLineContributors,
	hasByLineExtensions,
	shouldShowBylineReactions,
	contentSubType,
	spaceId,
	bylineClassificationFragmentRef,
	contentClassificationFragmentRef,
	reactionsContentFragmentRef,
	analyticsBylineFragmentRef,
	contentType,
}) => {
	const { versionOverride, embeddedContentRender } = useViewPageParams();
	return (
		<ByLineWithClassificationFragment
			contentId={contentId}
			versionOverride={versionOverride}
			embeddedContentRender={embeddedContentRender}
			hasByLineContributors={hasByLineContributors}
			hasByLineExtensions={hasByLineExtensions}
			shouldShowBylineReactions={shouldShowBylineReactions}
			contentType={contentType}
			contentSubType={contentSubType}
			spaceId={spaceId}
			bylineClassificationFragmentRef={bylineClassificationFragmentRef}
			contentClassificationFragmentRef={contentClassificationFragmentRef}
			reactionsContentFragmentRef={reactionsContentFragmentRef}
			analyticsBylineFragmentRef={analyticsBylineFragmentRef}
		/>
	);
};

const ContentTitleWithByLineQuery: FC<Props & ByLineSlot> = ({
	contentId,
	spaceKey,
	banner,
	headingContainer,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	contentType,
	meetingNotesAutomationDiscoveryFragmentRef,
	hasByLineContributors,
	hasByLineExtensions,
	shouldShowBylineReactions,
	contentSubType,
	spaceId,
	bylineClassificationFragmentRef,
	contentClassificationFragmentRef,
	reactionsContentFragmentRef,
	analyticsBylineFragmentRef,
	byLine,
	hasByLine = true,
}) => {
	const { versionOverride, embeddedContentRender } = useViewPageParams();
	const { isRovoLLMEnabled } = useSessionData();
	const shouldRenderMeetingNotesAutomationDiscovery =
		isRovoLLMEnabled &&
		contentType === 'page' &&
		!isEmbeddedPage &&
		fg('cc_automations_discovery_nudges');

	const { titleContentProperties } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});

	const isBylineCenterStyles =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	return (
		<ContentTitle
			banner={banner}
			contentId={contentId}
			spaceKey={spaceKey}
			headingContainer={headingContainer}
			hasCoverPicture={hasCoverPicture}
			pageWidthType={pageWidthType}
			isEmbeddedPage={isEmbeddedPage}
		>
			<Box
				xcss={[isBylineCenterStyles && BylineCenterStyles]}
				testId={
					isBylineCenterStyles ? 'centered-content-title-and-byline' : 'content-title-and-byline'
				}
			>
				{hasByLine && (
					<>
						{byLine ?? (
							<ByLineWithClassificationFragment
								contentId={contentId}
								versionOverride={versionOverride}
								embeddedContentRender={embeddedContentRender}
								hasByLineContributors={hasByLineContributors}
								hasByLineExtensions={hasByLineExtensions}
								shouldShowBylineReactions={shouldShowBylineReactions}
								contentType={contentType}
								contentSubType={contentSubType}
								spaceId={spaceId}
								bylineClassificationFragmentRef={bylineClassificationFragmentRef}
								contentClassificationFragmentRef={contentClassificationFragmentRef}
								reactionsContentFragmentRef={reactionsContentFragmentRef}
								analyticsBylineFragmentRef={analyticsBylineFragmentRef}
							/>
						)}
						{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
							<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
								<Box xcss={ApprovalBannerStyles}>
									<ApprovalBanner />
								</Box>
							</ErrorBoundary>
						)}
						{shouldRenderMeetingNotesAutomationDiscovery && (
							<LazyMeetingNotesAutomationDiscoveryFragment
								contentFragmentRef={meetingNotesAutomationDiscoveryFragmentRef}
								postOfficePlacementId="confluence-view-page-title-area-banner"
								WrapperComponent={TitleAreaBannerWrapper}
							/>
						)}
					</>
				)}
			</Box>
		</ContentTitle>
	);
};

const ContentTitleWithByLineFragmentReader: FC<FragmentProps & FragmentByLineSlot> = ({
	contentFragmentRef,
	titleSpaceFragmentRef,
	contentId,
	spaceKey,
	banner,
	headingContainer,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	contentType,
	byLineContributors,
	hasByLineContributors,
	hasByLineExtensions,
	shouldShowBylineReactions,
	contentSubType,
	spaceId,
	bylineClassificationFragmentRef,
	contentClassificationFragmentRef,
	reactionsContentFragmentRef,
	analyticsBylineFragmentRef,
	analyticsBylineQuerySource,
	renderByLine,
	hasByLine = true,
}) => {
	const content = useFragment(ContentTitleWithByLineFragment, contentFragmentRef);
	const { isRovoLLMEnabled } = useSessionData();
	const shouldRenderMeetingNotesAutomationDiscovery =
		isRovoLLMEnabled &&
		contentType === 'page' &&
		!isEmbeddedPage &&
		fg('cc_automations_discovery_nudges');
	const { titleContentProperties } = getTitleContentProperty({ content: { nodes: [content] } });
	const bannerWithContentFragmentRef = isValidElement(banner)
		? React.cloneElement(banner as React.ReactElement<{ contentFragmentRef?: typeof content }>, {
				contentFragmentRef: content,
			})
		: banner;

	const isBylineCenterStyles =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	return (
		<ContentTitleFromFragment
			banner={bannerWithContentFragmentRef}
			contentId={contentId}
			spaceKey={spaceKey}
			headingContainer={headingContainer}
			hasCoverPicture={hasCoverPicture}
			pageWidthType={pageWidthType}
			isEmbeddedPage={isEmbeddedPage}
			contentFragmentRef={content}
			spaceFragmentRef={titleSpaceFragmentRef}
		>
			<Box
				xcss={[isBylineCenterStyles && BylineCenterStyles]}
				testId={
					isBylineCenterStyles ? 'centered-content-title-and-byline' : 'content-title-and-byline'
				}
			>
				{hasByLine && (
					<>
						{renderByLine ? (
							renderByLine(content)
						) : (
							<ByLineFromFragmentWithClassificationFragment
								contentId={contentId}
								contentFragmentRef={content}
								bylineClassificationFragmentRef={bylineClassificationFragmentRef}
								contentContributors={byLineContributors}
								contentClassificationFragmentRef={contentClassificationFragmentRef}
								reactionsContentFragmentRef={reactionsContentFragmentRef}
								analyticsBylineFragmentRef={analyticsBylineFragmentRef}
								analyticsBylineQuerySource={analyticsBylineQuerySource}
								hasByLineContributors={hasByLineContributors}
								hasByLineExtensions={hasByLineExtensions}
								shouldShowBylineReactions={shouldShowBylineReactions}
								contentType={contentType}
								contentSubType={contentSubType}
								spaceId={spaceId}
							/>
						)}
						{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
							<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
								<Box xcss={ApprovalBannerStyles}>
									<ApprovalBanner />
								</Box>
							</ErrorBoundary>
						)}
						{shouldRenderMeetingNotesAutomationDiscovery && (
							<LazyMeetingNotesAutomationDiscoveryFragment
								contentFragmentRef={content}
								postOfficePlacementId="confluence-view-page-title-area-banner"
								WrapperComponent={TitleAreaBannerWrapper}
							/>
						)}
					</>
				)}
			</Box>
		</ContentTitleFromFragment>
	);
};
